import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Package,
  ChevronRight,
  RotateCcw,
  ShoppingBag,
  CircleCheck,
} from "lucide-react";
import Header from "../components/Header";
import Navbar from "../components/Navbar";
import TrustStrip from "../components/TrustStrip";
import { useAuth } from "../context/AuthContext";
import { useShop } from "../context/ShopContext";
import { fetchMyOrders } from "../api";
import { homeTrust } from "../mock";
import { toast } from "sonner";

const Orders = () => {
  const { user, loading } = useAuth();
  const { addToCart, setCartOpen, setModal } = useShop();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      setFetching(false);
      return;
    }
    fetchMyOrders()
      .then((d) => setOrders(d || []))
      .catch(() => setOrders([]))
      .finally(() => setFetching(false));
  }, [user, loading]);

  const reorder = (order) => {
    order.items.forEach((it) =>
      addToCart(
        { id: it.id, name: it.name, brand: it.brand, price: it.price, img: it.img },
        it.qty
      )
    );
    toast.success("Items added to cart");
    setCartOpen(true);
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "";
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header variant="home" />
      <Navbar />

      <div className="max-w-[900px] mx-auto px-3 lg:px-4 py-6">
        <div className="flex items-center gap-1.5 text-[11.5px] text-gray-500 mb-3">
          <Link to="/" className="hover:text-[#0e7a3d]">Home</Link>
          <ChevronRight size={12} />
          <span className="font-semibold text-gray-700">My Orders</span>
        </div>
        <h1 className="text-[24px] font-extrabold text-gray-800 mb-5 flex items-center gap-2">
          <Package size={24} className="text-[#12a150]" /> My Orders
        </h1>

        {!user ? (
          <div className="text-center py-20">
            <ShoppingBag size={40} className="text-[#12a150] mx-auto mb-3" />
            <div className="text-[16px] font-bold text-gray-800">Please login to view your orders</div>
            <button
              onClick={() => setModal("login")}
              className="mt-4 bg-[#12a150] hover:bg-[#0d8a40] text-white text-[13px] font-semibold rounded-full px-6 py-2.5"
            >
              Login / Register
            </button>
          </div>
        ) : fetching ? (
          <div className="text-center py-20 text-gray-400">Loading your orders...</div>
        ) : orders.length === 0 ? (
          <div className="text-center py-20">
            <ShoppingBag size={40} className="text-[#12a150] mx-auto mb-3" />
            <div className="text-[16px] font-bold text-gray-800">No orders yet</div>
            <div className="text-[13px] text-gray-400 mt-1">Your placed orders will appear here</div>
            <button
              onClick={() => navigate("/category/medicines")}
              className="mt-4 bg-[#12a150] hover:bg-[#0d8a40] text-white text-[13px] font-semibold rounded-full px-6 py-2.5"
            >
              Start Shopping
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((o) => (
              <div key={o.order_id} className="border border-gray-100 rounded-xl shadow-sm overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-2 bg-[#f6faf7] px-4 py-3 border-b border-gray-100">
                  <div>
                    <div className="text-[13px] font-bold text-gray-800">{o.order_id}</div>
                    <div className="text-[11px] text-gray-400">Placed on {fmtDate(o.created_at)}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1 text-[11.5px] font-semibold text-[#0e7a3d] bg-green-50 px-2.5 py-1 rounded-full">
                      <CircleCheck size={13} /> {o.status}
                    </span>
                    <button
                      onClick={() => reorder(o)}
                      className="flex items-center gap-1.5 bg-[#12a150] hover:bg-[#0d8a40] text-white text-[12px] font-semibold rounded-full px-3.5 py-1.5 transition-colors"
                    >
                      <RotateCcw size={13} /> Reorder
                    </button>
                  </div>
                </div>
                <div className="p-4 flex flex-wrap gap-4">
                  {o.items.map((it) => (
                    <Link
                      key={it.id}
                      to={`/product/${it.id}`}
                      className="flex items-center gap-2.5 hover:opacity-80"
                    >
                      <img src={it.img} alt={it.name} className="w-11 h-11 rounded-lg object-cover bg-gray-50" />
                      <div>
                        <div className="text-[12.5px] font-semibold text-gray-800 leading-tight">{it.name}</div>
                        <div className="text-[11px] text-gray-400">Qty {it.qty} · ₹{it.price}</div>
                      </div>
                    </Link>
                  ))}
                  <div className="ml-auto text-right">
                    <div className="text-[11px] text-gray-400">Total</div>
                    <div className="text-[16px] font-extrabold text-[#0e7a3d]">₹ {o.total.toFixed(2)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <TrustStrip items={homeTrust} cols={4} />
    </div>
  );
};

export default Orders;
